import React from 'react';
import clsx from 'clsx';
import { useCountUp } from '@/hooks/useCountUp';

export type HealthLabel = 'healthy' | 'watch' | 'at_risk' | 'dormant';

const LABELS: Record<HealthLabel, { text: string; cls: string; color: string }> = {
  healthy: { text: 'Healthy',  cls: 'bg-success/15 text-success', color: 'var(--color-success)' },
  watch:   { text: 'Watch',    cls: 'bg-warning/15 text-warning', color: 'var(--color-warning)' },
  at_risk: { text: 'At risk',  cls: 'bg-danger/15 text-danger',   color: 'var(--color-danger)' },
  dormant: { text: 'Dormant',  cls: 'bg-text-muted/15 text-text-muted', color: 'var(--color-text-muted)' },
};

export const healthMeta = (label: HealthLabel) => LABELS[label];

/** Radial score. `size` in px; the stroke draws in over ~1s. */
export const HealthRing: React.FC<{ score: number; label: HealthLabel; size?: number; stroke?: number }> =
  ({ score, label, size = 84, stroke = 8 }) => {
    const n = useCountUp(score, 1000);
    const r = (size - stroke) / 2;
    const c = 2 * Math.PI * r;
    const dash = c * (1 - Math.max(0, Math.min(100, n)) / 100);
    const meta = LABELS[label];
    return (
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--color-border)" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={r} fill="none"
            stroke={meta.color} strokeWidth={stroke} strokeLinecap="round"
            strokeDasharray={c} strokeDashoffset={dash}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-lg font-bold leading-none tabular-nums text-text-primary">{Math.round(n)}</span>
          <span className="mt-0.5 text-[9px] uppercase tracking-wide text-text-muted">score</span>
        </div>
      </div>
    );
  };

export const HealthCard: React.FC<{
  score: number; label: HealthLabel; reasons: string[];
  lastActivityAt: string | null; lastLoginAt: string | null; activeUsers: number; totalUsers: number;
  delay?: number;
}> = ({ score, label, reasons, lastActivityAt, lastLoginAt, activeUsers, totalUsers, delay = 0 }) => {
  const meta = LABELS[label];
  return (
    <div className="fade-up rounded-xl border border-border bg-surface p-5" style={{ animationDelay: `${delay}ms` }}>
      <div className="flex items-start gap-4">
        <HealthRing score={score} label={label} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-text-primary">Account health</h3>
            <span className={clsx('rounded-full px-2 py-0.5 text-2xs font-semibold', meta.cls)}>{meta.text}</span>
          </div>
          <dl className="mt-2 space-y-1 text-xs">
            <div className="flex justify-between gap-3"><dt className="text-text-muted">Last activity</dt><dd className="text-text-primary">{relative(lastActivityAt)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-text-muted">Last login</dt><dd className="text-text-primary">{relative(lastLoginAt)}</dd></div>
            <div className="flex justify-between gap-3"><dt className="text-text-muted">Active users (30d)</dt><dd className="tabular-nums text-text-primary">{activeUsers} / {totalUsers}</dd></div>
          </dl>
        </div>
      </div>
      {reasons.length > 0 && (
        <ul className="mt-4 space-y-1 border-t border-border pt-3">
          {reasons.map((r) => (
            <li key={r} className="flex items-start gap-2 text-xs text-text-secondary">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: meta.color }} />{r}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export function relative(iso: string | null | undefined): string {
  if (!iso) return 'Never';
  const ms = Date.now() - new Date(iso).getTime();
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'Just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h === 1 ? '' : 's'} ago`;
  const d = Math.floor(h / 24);
  if (d < 31) return `${d} day${d === 1 ? '' : 's'} ago`;
  return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
